import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { logisticsRequestsApi } from '../../api/logistics';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { LOGISTICS_STATUS_LABELS } from './LogisticsRequestsListPage';
import { LOGISTICS_REQUEST_STAGES } from '../../config/workflow-stages';
import { formatDate } from '../../utils/formatDate';

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  vehicle: 'Vehicle', driver: 'Driver', logistics_officer: 'Logistics Officer', vendor: 'Vendor',
  accommodation: 'Accommodation', venue: 'Venue', equipment: 'Equipment', dispatch_officer: 'Dispatch Officer', other: 'Other',
};

export function LogisticsRequestWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const logisticsRequestId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: requestData, isError } = useQuery({ queryKey: ['logistics-request', logisticsRequestId], queryFn: () => logisticsRequestsApi.get(logisticsRequestId) });
  const logisticsRequest = requestData?.data;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['logistics-request', logisticsRequestId] });
  const { data: docsData } = useQuery({ queryKey: ['logistics-request-documents', logisticsRequestId], queryFn: () => genericDocumentApi.listFor('logistics_request', logisticsRequestId) });

  const submitMutation = useMutation({
    mutationFn: () => logisticsRequestsApi.submit(logisticsRequestId),
    onSuccess: () => { showSuccess('Logistics request submitted.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { register: registerDecision, handleSubmit: handleDecisionSubmit, reset: resetDecision } = useForm<{ comment: string }>();
  const decideMutation = useMutation({
    mutationFn: ({ decision, comment }: { decision: 'approved' | 'rejected'; comment: string }) => logisticsRequestsApi.decide(logisticsRequestId, decision, comment),
    onSuccess: (result) => { showSuccess(`Decision recorded (${result.data.status}).`); resetDecision(); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => logisticsRequestsApi.cancel(logisticsRequestId, reason),
    onSuccess: () => { showSuccess('Logistics request cancelled.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { register: registerItem, handleSubmit: handleItemSubmit, reset: resetItem } = useForm<{ description: string; quantity: number; unit?: string }>();
  const { data: itemsData } = useQuery({ queryKey: ['logistics-request-items', logisticsRequestId], queryFn: () => logisticsRequestsApi.listItems(logisticsRequestId) });
  const addItemMutation = useMutation({
    mutationFn: (input: { description: string; quantity: number; unit?: string }) => logisticsRequestsApi.addItem(logisticsRequestId, input),
    onSuccess: () => { showSuccess('Item added.'); resetItem(); queryClient.invalidateQueries({ queryKey: ['logistics-request-items', logisticsRequestId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [assignForm, setAssignForm] = useState({ resourceType: 'vehicle', vehicleId: '', employeeId: '', vendorName: '', assignmentDate: '' });
  const { data: assignmentsData } = useQuery({ queryKey: ['logistics-request-assignments', logisticsRequestId], queryFn: () => logisticsRequestsApi.listAssignments(logisticsRequestId) });
  const assignMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => logisticsRequestsApi.createAssignment(logisticsRequestId, input),
    onSuccess: () => { showSuccess('Resource assigned.'); queryClient.invalidateQueries({ queryKey: ['logistics-request-assignments', logisticsRequestId] }); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this logistics request. Please try refreshing the page.</div></div>;
  if (!logisticsRequest) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  const items = itemsData?.data ?? [];
  const assignments = assignmentsData?.data ?? [];

  return (
    <div className="page">
      <RecordHeader
        title={logisticsRequest.request_reference}
        readOnly={false}
        entityType="logistics_request"
        entityId={logisticsRequestId}
        fields={[
          { label: 'Purpose', value: logisticsRequest.purpose },
          { label: 'Required Date', value: formatDate(logisticsRequest.required_date) },
          { label: 'Priority', value: logisticsRequest.priority },
          { label: 'Status', value: <StatusBadge status={logisticsRequest.status} label={LOGISTICS_STATUS_LABELS[logisticsRequest.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                <SectionHeader title="Details" />
                <dl className="detail-grid">
                  <dt>Purpose</dt><dd>{logisticsRequest.purpose}</dd>
                  <dt>Business Justification</dt><dd>{logisticsRequest.business_justification ?? '-'}</dd>
                  <dt>Priority</dt><dd>{logisticsRequest.priority}</dd>
                  <dt>Delivery Location</dt><dd>{logisticsRequest.delivery_location ?? '-'}</dd>
                  <dt>Estimated Cost</dt><dd>{logisticsRequest.estimated_cost ? `${logisticsRequest.estimated_cost_currency} ${logisticsRequest.estimated_cost}` : '-'}</dd>
                </dl>

                {logisticsRequest.status === 'draft' && (
                  <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit for Approval</button>
                )}
                {!['rejected', 'cancelled', 'closed', 'archived'].includes(logisticsRequest.status) && (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); const reason = (e.target as HTMLFormElement).reason.value; if (reason) cancelMutation.mutate(reason); }}>
                    <input name="reason" placeholder="Reason for cancellation" />
                    <button type="submit" disabled={cancelMutation.isPending}>Cancel Request</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'items', label: 'Items', content: (
              <div>
                <SectionHeader title="Requested Items" />
                <ul>
                  {items.map((i) => <li key={i.id}>{i.description} — {i.quantity} {i.unit ?? ''}</li>)}
                </ul>
                <form className="inline-form" onSubmit={handleItemSubmit((d) => addItemMutation.mutate({ ...d, quantity: Number(d.quantity) }))}>
                  <input placeholder="Description" {...registerItem('description', { required: true })} />
                  <input type="number" step="0.01" placeholder="Quantity" {...registerItem('quantity', { required: true })} />
                  <input placeholder="Unit" {...registerItem('unit')} />
                  <button type="submit" disabled={addItemMutation.isPending}>Add Item</button>
                </form>
              </div>
            ),
          },
          {
            key: 'approval', label: 'Approval', content: (
              <div>
                <SectionHeader title="Approval" />
                {['submitted', 'under_review'].includes(logisticsRequest.status) && (
                  <form className="inline-form" onSubmit={handleDecisionSubmit((d) => decideMutation.mutate({ decision: 'approved', comment: d.comment }))}>
                    <textarea placeholder="Comment (required)" {...registerDecision('comment', { required: true })} />
                    <button type="submit" disabled={decideMutation.isPending}>Approve</button>
                    <button type="button" onClick={handleDecisionSubmit((d) => decideMutation.mutate({ decision: 'rejected', comment: d.comment }))} disabled={decideMutation.isPending}>Reject</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'assignments', label: 'Assignments', content: (
              <div>
                <SectionHeader title="Assigned Resources" />
                <ul>
                  {assignments.map((a) => (
                    <li key={a.id}>{RESOURCE_TYPE_LABELS[a.resource_type] ?? a.resource_type}{a.vendor_name ? ` — ${a.vendor_name}` : ''}{a.description ? ` — ${a.description}` : ''} · {formatDate(a.assignment_date)} · <StatusBadge status={a.status} /></li>
                  ))}
                </ul>
                {['approved', 'in_progress'].includes(logisticsRequest.status) && (
                  <form className="inline-form" onSubmit={(e) => {
                    e.preventDefault();
                    const input: Record<string, unknown> = { resourceType: assignForm.resourceType, assignmentDate: assignForm.assignmentDate };
                    if (assignForm.resourceType === 'vehicle' && assignForm.vehicleId) input.vehicleId = Number(assignForm.vehicleId);
                    if (['driver', 'logistics_officer', 'dispatch_officer'].includes(assignForm.resourceType) && assignForm.employeeId) input.employeeId = Number(assignForm.employeeId);
                    if (['vendor', 'accommodation', 'venue'].includes(assignForm.resourceType) && assignForm.vendorName) input.vendorName = assignForm.vendorName;
                    assignMutation.mutate(input);
                  }}>
                    <select value={assignForm.resourceType} onChange={(e) => setAssignForm((s) => ({ ...s, resourceType: e.target.value }))}>
                      {Object.entries(RESOURCE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {assignForm.resourceType === 'vehicle' && <input placeholder="Vehicle ID" value={assignForm.vehicleId} onChange={(e) => setAssignForm((s) => ({ ...s, vehicleId: e.target.value }))} />}
                    {['driver', 'logistics_officer', 'dispatch_officer'].includes(assignForm.resourceType) && (
                      <EmployeePicker value={assignForm.employeeId} onChange={(e) => setAssignForm((s) => ({ ...s, employeeId: e.target.value }))} />
                    )}
                    {['vendor', 'accommodation', 'venue'].includes(assignForm.resourceType) && <input placeholder="Vendor/venue name" value={assignForm.vendorName} onChange={(e) => setAssignForm((s) => ({ ...s, vendorName: e.target.value }))} />}
                    <input type="date" value={assignForm.assignmentDate} onChange={(e) => setAssignForm((s) => ({ ...s, assignmentDate: e.target.value }))} required />
                    <button type="submit" disabled={assignMutation.isPending}>Assign</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('logistics_request', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('logistics_request', logisticsRequestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('logistics_request', documentId)}
                queryKeyToInvalidate={['logistics-request-documents', logisticsRequestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="logistics_request" entityId={logisticsRequestId} actionStageMap={LOGISTICS_REQUEST_STAGES} statusLabels={LOGISTICS_STATUS_LABELS} />
                <CommentsThread entityType="logistics_request" entityId={logisticsRequestId} section="logistics" heading="Comments for this Request" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
